/*
 * The head a public page advertises once JavaScript has run.
 *
 * WHY IT IS SHARED. src/scripts/injectHeadTags.js builds the same tag set at
 * build time, from src/data/publicRoutes.js, for crawlers that run no scripts.
 * Google runs scripts and reads what this file produces. When the two sets
 * disagree, the same URL describes itself two different ways depending on who
 * fetched it -- which is how the site ended up with `og:image` on two pages out
 * of eight, seven views emitting the `name="og:*"` attribute no Open Graph
 * crawler reads, and a production origin hardcoded into a head that also ships
 * to QA. Building the runtime set in one place is what makes "the two heads
 * agree" a property of the code rather than of eight copy-pastes.
 *
 * THE TWO SETS ARE THE SAME SET, not an overlap. Everything the injector writes
 * is written here too, down to og:site_name, og:locale, og:image:alt and the
 * card's dimensions -- values neither the tab nor the reader ever shows. That
 * completeness is what lets src/scripts/staticHeadDedupe.js hand the whole head
 * over to @unhead/vue on the first render: a tag the client did not claim would
 * be a tag frozen on whichever page the reader happened to arrive on.
 *
 * NO COPY LIVES HERE. Callers pass their own title and description, normally as
 * computed refs over their existing i18n keys: locale bundles load lazily, so a
 * string read at mount would freeze the English fallback into the tab of a
 * reader whose own bundle landed a moment later. unref() below is what lets a
 * caller pass either a ref or the plain authored English a blog post carries.
 */
import { computed, unref } from "vue";
import { brandedTitle } from "@/scripts/pageTitle.js";
import { siteUrl } from "@/data/siteOrigin.js";
import {
  SOCIAL_CARD_PATH,
  SITE_NAME,
  OG_LOCALE,
  TWITTER_CARD,
  imageDimensions,
  normalizeHeadCopy,
} from "@/data/socialMeta.js";

/**
 * Build the argument for `useHead` on a public, indexable page.
 *
 * @param {object} page
 * @param {import("vue").Ref<string>|string} page.title  page name, UNBRANDED
 * @param {import("vue").Ref<string>|string} page.description  meta description
 * @param {string} page.path  this page's router path, root-relative
 * @param {string} [page.image]  root-relative og:image, defaults to the card
 * @param {string} [page.type]  og:type; "article" for blog posts
 * @returns {object} the object to hand straight to useHead()
 */
export function publicPageHead({
  title,
  description,
  path,
  image = SOCIAL_CARD_PATH,
  type = "website",
}) {
  // Absolute, and env-aware. Twitter/X and several preview crawlers reject a
  // relative og:image or og:url outright, and a hardcoded production origin is
  // what made previews impossible to validate on QA before a prod release.
  const url = siteUrl(path);
  const imageUrl = siteUrl(image);
  const dimensions = imageDimensions(image);

  // Through the same normalizer the injector uses, and still computed, so the
  // laziness survives: a locale value that wraps onto two lines, or a blog
  // description that ends in a space, would otherwise render one way in the
  // served file and another way after hydration.
  const pageTitle = computed(() => normalizeHeadCopy(unref(title)));
  const pageDescription = computed(() => normalizeHeadCopy(unref(description)));

  const meta = [
    // description stays on `name`: it is a plain HTML meta, not Open Graph,
    // and the e2e smoke test locates it that way.
    { name: "description", content: pageDescription },
    // Open Graph is defined on `property`. `name="og:*"` is not Open Graph at
    // all -- facebookexternalhit, linkedinbot, slackbot and discordbot skip
    // it, which is why most of this site's links have never had a card.
    { property: "og:title", content: pageTitle },
    { property: "og:description", content: pageDescription },
    { property: "og:type", content: type },
    { property: "og:url", content: url },
    { property: "og:image", content: imageUrl },
    // Reuses the page's own title rather than authoring alt text, exactly as
    // the static head does: new copy would need 31 locales and an allowlist
    // entry for a string only screen readers on someone else's site reach.
    { property: "og:image:alt", content: pageTitle },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:locale", content: OG_LOCALE },
    // Twitter's own tags are defined on `name`, unlike Open Graph.
    { name: "twitter:card", content: TWITTER_CARD },
    { name: "twitter:title", content: pageTitle },
    { name: "twitter:description", content: pageDescription },
    { name: "twitter:image", content: imageUrl },
  ];

  // Only for an image whose size is part of its contract, matching the static
  // head tag for tag. Emitting them conditionally is also what keeps them
  // honest across a client-side navigation: unhead removes a tag it rendered
  // once the new page stops asking for it, so moving from the shared card to a
  // page with its own artwork drops the dimensions rather than misdescribing
  // the new image.
  if (dimensions) {
    meta.push({ property: "og:image:width", content: String(dimensions.width) });
    meta.push({ property: "og:image:height", content: String(dimensions.height) });
  }

  return {
    // Branded, because a tab title sits in a bar beside other sites. og:title
    // above is deliberately bare: the preview card already prints og:site_name
    // next to the headline, and the static head brands the two the same way,
    // so Google does not watch the headline change when the page hydrates.
    title: computed(() => brandedTitle(pageTitle.value)),
    // The tag that decides which URL Google indexes. The build-time injector
    // writes one too; this one keeps it correct after client-side navigation,
    // when the static tag would still describe the page the reader arrived on.
    link: [{ rel: "canonical", href: url }],
    meta,
  };
}
